import React, { useState } from 'react';
import { coreService } from '../../../../../../services/core/core-service';
import { format } from 'date-fns';
import { Button, Flex, Input, Tooltip } from 'antd';
import { useToast } from '../../../../../../hooks/useToast';
import { colors } from '../../../../../../theme/colors';
import Styles from './styles';
import ImportModal from '../ImportModal';
import RecapStatus from '../../../../../../features/home/tabs/RecapsTab/components/RecapStatus';
import { ClariCall } from '../../../../../../services/types';

const { Container } = Styles;

interface CallsProps {
  hideModal: () => void;
  showModal: () => void;
}

interface CallProps {
  title: string;
  scheduled?: string;
  duration?: number;
  status?: string;
  subStatus?: string;
  recapId?: string;
  importedBy?: string;
}

export const Call: React.FC<CallProps> = ({
  title,
  scheduled,
  duration,
  status,
  subStatus,
  recapId,
  importedBy,
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        lineHeight: 1.57,
        marginRight: '10px',
        width: '650px',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto auto',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        {status === 'READY' && recapId ? (
          <a
            style={{ fontSize: '14px', color: colors.primary[500] }}
            href={`/recap/${recapId}`}
            target="_blank"
          >
            {title}
          </a>
        ) : (
          <div style={{ fontSize: '14px' }}>{title}</div>
        )}

        {!['IMPORTING', 'IMPORTED', ''].includes(status!.toUpperCase()) ? (
          <RecapStatus status={status!} subStatus={subStatus || ''} />
        ) : (
          <div style={{ fontSize: '12px' }}>{status}</div>
        )}
      </div>

      <div style={{ fontSize: '11px', display: 'flex', gap: '5px', alignItems: 'center' }}>
        <div>{format(new Date(scheduled || ''), 'MMM dd, yyyy hh:mm')}</div>
        <div>-</div>
        <div>{Math.floor((duration || 0) % 60)} minutes</div>
        {importedBy && <div>-</div>}
        {importedBy && (
          <Tooltip
            overlayInnerStyle={{ width: '400px', whiteSpace: 'pre-wrap' }}
            title={`If you don't have access to this meeting and you didn't import it, two things might be happening:\n1. The meeting might be set to "invitees only." Ask the person who imported it to change the visibility to "workspace".\n2. You might not be on the invitees list.`}
          >
            <div style={{ textDecoration: 'underline' }}>Imported by: {importedBy}</div>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export const Calls: React.FC<CallsProps> = ({ showModal }) => {
  const [data, setData] = React.useState<{ cursor?: string; calls: ClariCall[] }>({ calls: [] });
  const [showImportModal, setShowImportModal] = React.useState(false);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [importing, setImporting] = React.useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [attendee, setAttendee] = useState('');
  const importLabel = `Import ${selected.length} ${selected.length === 1 ? 'call' : 'calls'}`;

  const { success, error } = useToast();

  const loadCalls = async () => {
    if (attendee) {
      setIsFetching(true);
      coreService
        .getClariMeetings([attendee])
        .then(res => {
          const newCalls = res?.calls || [];
          const newCallsIds = newCalls.map((c: { id: string }) => c.id);

          const sortedData = [
            ...[...data.calls].filter(c => !newCallsIds.includes(c.id)),
            ...newCalls,
          ]
            .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
            .map(call => ({
              ...call,
              status: call.imported ? (call.eventId ? 'Imported' : 'Importing') : null,
            }));

          setData({
            cursor: res?.records?.cursor,
            calls: sortedData,
          });
        })
        .finally(() => setIsFetching(false));
    }
  };

  const importCalls = async (callId: string) => {
    setImporting(true);
    coreService
      .importClariCall(callId)
      .then(() => {
        setData({
          cursor: data.cursor,
          calls: data.calls.map(call => {
            if (call.id === callId) {
              return { ...call, status: 'Importing' };
            }
            return call;
          }),
        });
        setSelected([]);
        success('Calls imported successfully');
      })
      .catch(() => {
        error('Failed to import calls');
      })
      .finally(() => setImporting(false));
  };

  return (
    <Container>
      <Flex vertical gap={8} style={{ overflow: 'auto', width: '100%' }}>
        <Flex gap={4}>
          <Input
            placeholder="Attendee"
            value={attendee}
            onChange={ev => setAttendee(ev.target.value)}
          />
          <Button disabled={isFetching} onClick={() => loadCalls()}>
            Search
          </Button>
        </Flex>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'scroll',
            gap: '10px',
            width: '100%',
          }}
        >
          {data?.calls?.map(call =>
            !call?.status ? (
              <Flex gap={4}>
                <Call
                  key={call.id + '-call'}
                  title={call?.title}
                  scheduled={call?.startTime}
                  duration={call?.duration}
                  status={call?.status || ''}
                  importedBy={call?.importedBy}
                />
                <Button type="link" onClick={() => importCalls(call.id)}>
                  Import
                </Button>
              </Flex>
            ) : (
              <Call
                key={call.id + '-call'}
                title={call?.title}
                scheduled={call?.startTime}
                duration={call?.duration}
                status={call?.status}
                subStatus={call?.subStatus || ''}
                recapId={call?.recapId}
                importedBy={call?.importedBy}
              />
            ),
          )}
        </div>
      </Flex>

      {/*<Button
        type="default"
        disabled={selected.length === 0 || importing}
        style={{ width: '200px' }}
        loading={importing}
        onClick={() => {
          hideModal();
          setShowImportModal(true);
        }}
      >
        {importLabel}
      </Button>*/}

      <ImportModal
        shouldShow={showImportModal}
        loading={importing}
        closeModal={() => {
          setShowImportModal(false);
          showModal();
        }}
        confirmButtonText={importLabel}
        onConfirm={() => {
          setShowImportModal(false);
          showModal();
        }}
      />
    </Container>
  );
};
